import { createContext, useContext, useEffect, useState } from 'react';

interface WatermarkContextValue {
  workspaceId?: string;
  originalUrl: (assetId: string) => Promise<string>;
  downloadUrl: (assetId: string) => Promise<string>;
  hidden: boolean;
  toggle: () => void;
  register: (url: string, assetId: string) => void;
  assetForUrl: (url: string) => string | undefined;
  rejectOriginal: () => void;
}
const empty: WatermarkContextValue = {
  originalUrl: async () => {
    throw new Error('No workspace');
  },
  downloadUrl: async () => {
    throw new Error('No workspace');
  },
  hidden: false,
  toggle: () => undefined,
  register: () => undefined,
  assetForUrl: () => undefined,
  rejectOriginal: () => undefined,
};
export const ImageWatermarkContext =
  createContext<WatermarkContextValue>(empty);
export const useImageWatermark = () => useContext(ImageWatermarkContext);

export const useImageVariant = (
  fallback?: string,
  explicitAssetId?: string
): string | undefined => {
  const {
    workspaceId,
    hidden,
    register,
    assetForUrl,
    rejectOriginal,
    originalUrl,
  } = useImageWatermark();
  const assetId =
    explicitAssetId ?? (fallback ? assetForUrl(fallback) : undefined);
  const [resolved, setResolved] = useState<{ key: string; url: string } | null>(
    null
  );
  const key = `${workspaceId}:${assetId}:${hidden}`;
  useEffect(() => {
    if (explicitAssetId && fallback) register(fallback, explicitAssetId);
  }, [explicitAssetId, fallback, register]);
  useEffect(() => {
    if (!workspaceId || !assetId || !hidden) return undefined;
    let canceled = false;
    void originalUrl(assetId)
      .then(url => {
        if (!canceled) setResolved({ key, url });
      })
      .catch(() => {
        if (!canceled) rejectOriginal();
      });
    return () => {
      canceled = true;
    };
  }, [workspaceId, assetId, hidden, key, rejectOriginal, originalUrl]);
  return hidden && resolved?.key === key ? resolved.url : fallback;
};
